import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  CloudArrowUpIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { youtubeApi, YouTubePublishJobStatus } from '@/services/youtubeApi';
import { YouTubeUploadEntry } from '@/services/videoApi';

interface YouTubePublishModalProps {
  sourceType: 'translation' | 'upload' | 'clip';
  sourceId: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultTags?: string[];
  existingUploads?: YouTubeUploadEntry[];
  // Present when the source clip already has generated subtitles — offered
  // as a one-click "attach existing captions" option instead of requiring a
  // fresh SRT upload.
  defaultCaptionsSrtUrl?: string;
  onClose: () => void;
  onPublishStart?: () => void;
  onPublishJobQueued?: (publishJobId: string) => void;
  onPublished?: (videoUrl: string) => void;
  onPublishError?: () => void;
}

const PRIVACY_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'unlisted', label: 'Unlisted' },
  { value: 'public', label: 'Public' },
];

const CATEGORY_OPTIONS = [
  { value: '1', label: 'Film & Animation' },
  { value: '2', label: 'Autos & Vehicles' },
  { value: '10', label: 'Music' },
  { value: '15', label: 'Pets & Animals' },
  { value: '17', label: 'Sports' },
  { value: '18', label: 'Short Movies' },
  { value: '19', label: 'Travel & Events' },
  { value: '20', label: 'Gaming' },
  { value: '21', label: 'Videoblogging' },
  { value: '22', label: 'People & Blogs' },
  { value: '23', label: 'Comedy' },
  { value: '24', label: 'Entertainment' },
  { value: '25', label: 'News & Politics' },
  { value: '26', label: 'Howto & Style' },
  { value: '27', label: 'Education' },
  { value: '28', label: 'Science & Technology' },
  { value: '29', label: 'Nonprofits & Activism' },
  { value: '30', label: 'Movies' },
  { value: '31', label: 'Anime/Animation' },
  { value: '32', label: 'Action/Adventure' },
  { value: '33', label: 'Classics' },
  { value: '34', label: 'Comedy' },
  { value: '35', label: 'Documentary' },
  { value: '36', label: 'Drama' },
  { value: '37', label: 'Family' },
  { value: '38', label: 'Foreign' },
  { value: '39', label: 'Horror' },
  { value: '40', label: 'Sci-Fi/Fantasy' },
  { value: '41', label: 'Thriller' },
  { value: '42', label: 'Shorts' },
  { value: '43', label: 'Shows' },
  { value: '44', label: 'Trailers' },
];

const YouTubePublishModal: React.FC<YouTubePublishModalProps> = ({
  sourceType,
  sourceId,
  defaultTitle = '',
  defaultDescription = '',
  defaultTags = [],
  existingUploads = [],
  defaultCaptionsSrtUrl,
  onClose,
  onPublishStart,
  onPublishJobQueued,
  onPublished,
  onPublishError,
}) => {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [tags, setTags] = useState(defaultTags.join(', '));
  const [privacyStatus, setPrivacyStatus] = useState<'private' | 'unlisted' | 'public'>('private');
  const [categoryId, setCategoryId] = useState('22');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [publishMode, setPublishMode] = useState<'new' | 'overwrite'>('new');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  // Unset (null) by default — YouTube requires this be accurate, so the user
  // must make an explicit choice rather than silently defaulting to "No".
  const [selfDeclaredMadeForKids, setSelfDeclaredMadeForKids] = useState<boolean | null>(null);
  const [playlistId, setPlaylistId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [license, setLicense] = useState<'youtube' | 'creativeCommon'>('youtube');
  const [embeddable, setEmbeddable] = useState(true);
  const [publicStatsViewable, setPublicStatsViewable] = useState(true);
  const [useOrgBranding, setUseOrgBranding] = useState(true);
  const [applyFadeOut, setApplyFadeOut] = useState(false);
  const [useExistingCaptions, setUseExistingCaptions] = useState(!!defaultCaptionsSrtUrl);
  const [captionsFile, setCaptionsFile] = useState<File | null>(null);
  const [publishJobId, setPublishJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<YouTubePublishJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['youtube-accounts'],
    queryFn: () => youtubeApi.getAccounts(),
  });

  useEffect(() => {
    const accounts = accountsData?.accounts || [];
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0]._id);
    }
  }, [accountsData, selectedAccountId]);

  const { data: playlistsData, isLoading: playlistsLoading } = useQuery({
    queryKey: ['youtube-playlists', selectedAccountId],
    queryFn: () => youtubeApi.getPlaylists(selectedAccountId),
    enabled: !!selectedAccountId,
  });
  const playlists = playlistsData?.playlists || [];

  useEffect(() => {
    // Reset the playlist choice when switching accounts — a playlist ID
    // from one channel is meaningless (and would 404) on another.
    setPlaylistId('');
  }, [selectedAccountId]);

  useEffect(() => {
    if (thumbnailFile) {
      const url = URL.createObjectURL(thumbnailFile);
      setThumbnailPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [thumbnailFile]);

  // Set right before polling starts so the completion toast can tell a truly
  // published video apart from one uploaded-but-still-scheduled (native
  // publishAt) — both reach job.status === 'completed' from our side, but
  // only the former is actually public on YouTube yet.
  const scheduledForRef = useRef<string | undefined>(undefined);

  const startPolling = useCallback((jobId: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { job } = await youtubeApi.pollPublishJob(jobId);
        setJobStatus(job.status);
        if (job.status === 'completed') {
          stopPolling();
          const stillScheduled = scheduledForRef.current && new Date(scheduledForRef.current).getTime() > Date.now();
          toast.success(
            (t) => (
              <span className="flex items-center gap-2">
                {stillScheduled
                  ? `Uploaded — will go live on YouTube at ${new Date(scheduledForRef.current!).toLocaleString()}`
                  : 'Video published to YouTube!'}
                {job.videoUrl && (
                  <a
                    href={job.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                    onClick={() => toast.dismiss(t.id)}
                  >
                    View
                  </a>
                )}
              </span>
            ),
            { duration: 6000 }
          );
          onPublished?.(job.videoUrl || '');
          onClose();
        } else if (job.status === 'failed') {
          stopPolling();
          toast.error(job.error || 'Failed to publish video to YouTube');
          onPublishError?.();
          setPublishJobId(null);
          setJobStatus(null);
        }
      } catch {
        // transient poll error — keep retrying
      }
    }, 4000);
  }, [stopPolling, onPublished, onPublishError, onClose]);

  const publishMutation = useMutation({
    mutationFn: async () => {
      onPublishStart?.();
      const token = localStorage.getItem('token');
      let thumbnailUrl = thumbnailPreview;
      if (thumbnailFile) {
        const formData = new FormData();
        formData.append('thumbnail', thumbnailFile);
        const response = await fetch('/api/youtube/upload-thumbnail', {
          method: 'POST',
          body: formData,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const result = await response.json();
        thumbnailUrl = result.thumbnailUrl;
      }

      let captionsSrtUrl: string | undefined = useExistingCaptions ? defaultCaptionsSrtUrl : undefined;
      if (!useExistingCaptions && captionsFile) {
        const formData = new FormData();
        formData.append('captions', captionsFile);
        const response = await fetch('/api/youtube/upload-captions', {
          method: 'POST',
          body: formData,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const result = await response.json();
        captionsSrtUrl = result.captionsSrtUrl;
      }

      return youtubeApi.publish({
        sourceType,
        sourceId,
        accountId: selectedAccountId,
        title,
        description,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        privacyStatus,
        categoryId,
        thumbnailUrl: thumbnailUrl || undefined,
        publishMode: existingUploads.length > 0 ? publishMode : 'new',
        scheduledFor: scheduleEnabled && scheduledFor ? new Date(scheduledFor).toISOString() : undefined,
        selfDeclaredMadeForKids: !!selfDeclaredMadeForKids,
        playlistId: playlistId || undefined,
        license,
        embeddable,
        publicStatsViewable,
        captionsSrtUrl,
        useOrgBranding,
        applyFadeOut,
      });
    },
    onSuccess: (data) => {
      if (data.success && data.publishJobId) {
        onPublishJobQueued?.(data.publishJobId);
        scheduledForRef.current = data.scheduledFor;
        if (data.scheduledFor) {
          toast.success(`Uploading now — will go live on YouTube at ${new Date(data.scheduledFor).toLocaleString()}`);
        }
        setPublishJobId(data.publishJobId);
        setJobStatus('pending');
        startPolling(data.publishJobId);
      } else {
        toast.error(data.message || 'Failed to queue publish job');
        onPublishError?.();
      }
    },
    onError: () => {
      toast.error('Failed to submit publish request');
      onPublishError?.();
    },
  });

  const isPublishing = publishMutation.isLoading || (!!publishJobId && jobStatus !== 'completed' && jobStatus !== 'failed');
  const publishStatusLabel = jobStatus === 'processing' ? 'Uploading to YouTube…' : jobStatus === 'pending' ? 'Queued…' : jobStatus === 'scheduled' ? 'Scheduled…' : 'Publishing…';

  const accounts = accountsData?.accounts || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <VideoCameraIcon className="h-5 w-5 text-red-600" />
            Publish to YouTube
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            title={isPublishing ? 'Publishing in progress — will continue in background' : 'Close'}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {isPublishing && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
            <ArrowPathIcon className="h-4 w-4 animate-spin flex-shrink-0" />
            <span>{publishStatusLabel} You can close this window and the upload will continue in the background.</span>
          </div>
        )}

        <div className="p-6 space-y-5">
          {accountsLoading ? (
            <div className="animate-pulse h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ) : accounts.length === 0 ? (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
              No YouTube accounts linked. Connect an account in{' '}
              <a href="/app/integrations" className="font-medium underline">
                Integrations
              </a>{' '}
              first.
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                YouTube Account *
              </label>
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.channelTitle}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter video title"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{title.length}/100</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter video description"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{description.length}/5000</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Comma separated tags</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Privacy
              </label>
              <select
                value={privacyStatus}
                onChange={(e) => setPrivacyStatus(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {PRIVACY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Made for kids *
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    checked={selfDeclaredMadeForKids === true}
                    onChange={() => setSelfDeclaredMadeForKids(true)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    checked={selfDeclaredMadeForKids === false}
                    onChange={() => setSelfDeclaredMadeForKids(false)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">YouTube requires this to be set accurately.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Playlist
              </label>
              <select
                value={playlistId}
                onChange={(e) => setPlaylistId(e.target.value)}
                disabled={playlistsLoading || playlists.length === 0}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              >
                <option value="">No playlist</option>
                {playlists.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              {showAdvanced ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
              Advanced settings
            </button>
            {showAdvanced && (
              <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">License</label>
                  <select
                    value={license}
                    onChange={(e) => setLicense(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="youtube">Standard YouTube License</option>
                    <option value="creativeCommon">Creative Commons — Attribution</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={embeddable}
                    onChange={(e) => setEmbeddable(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Allow embedding on other websites
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={publicStatsViewable}
                    onChange={(e) => setPublicStatsViewable(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Show video stats publicly (views, likes)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={useOrgBranding}
                    onChange={(e) => setUseOrgBranding(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Use my organization&apos;s default intro/outro (if configured)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={applyFadeOut}
                    onChange={(e) => setApplyFadeOut(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Fade out at the end
                </label>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              Schedule publishing
            </label>
            {scheduleEnabled && (
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Publish date and time</label>
                <input
                  type="datetime-local"
                  value={scheduledFor}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}
          </div>

          {existingUploads.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Publish Mode
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    value="new"
                    checked={publishMode === 'new'}
                    onChange={() => setPublishMode('new')}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Create new video</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    value="overwrite"
                    checked={publishMode === 'overwrite'}
                    onChange={() => setPublishMode('overwrite')}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Overwrite existing</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {publishMode === 'overwrite'
                  ? 'Updates title, description, privacy and thumbnail of the most recently published video.'
                  : 'Uploads a brand new YouTube video.'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Thumbnail
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <CloudArrowUpIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Upload thumbnail</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setThumbnailFile(file);
                  }}
                />
              </label>
              {thumbnailPreview && (
                <img src={thumbnailPreview} alt="Thumbnail preview" className="h-16 w-28 object-cover rounded-md border border-gray-200 dark:border-gray-700" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Captions
            </label>
            {defaultCaptionsSrtUrl ? (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={useExistingCaptions}
                  onChange={(e) => setUseExistingCaptions(e.target.checked)}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                Attach this clip&apos;s existing captions
              </label>
            ) : (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <CloudArrowUpIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Upload SRT file</span>
                  <input
                    type="file"
                    accept=".srt"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setCaptionsFile(file);
                    }}
                  />
                </label>
                {captionsFile && <span className="text-xs text-gray-500 dark:text-gray-400">{captionsFile.name}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => publishMutation.mutate()}
            disabled={isPublishing || accounts.length === 0 || !title.trim() || !selectedAccountId || (scheduleEnabled && !scheduledFor) || selfDeclaredMadeForKids === null}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isPublishing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                {publishStatusLabel}
              </>
            ) : (
              <>
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                {scheduleEnabled ? 'Schedule for YouTube' : 'Publish to YouTube'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YouTubePublishModal;
